/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router";

import { Track } from "@highnote/server/src/core/entities";
import { useSpaceContext } from "App/common/useSpace";
import { ViewingSpaceId } from "App/modules/viewingSpace";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { TRACK_EDITOR_TAB, TrackEditor } from "./TrackEditor";
import { TrackContextProvider, useTrack } from "./useEntities/useTrack";
import { FileEditorProvider } from "./useFileEditor";

interface openTrackEditorConfig {
  track: Track;
  initialTab?: TRACK_EDITOR_TAB;
  /**
   * The space the track is being opened FROM, if the caller knows it (a row
   * in a space's list, the track page). Omit for lists with no containing
   * space, or for a track from another space (move/add dialogs): the
   * server then falls back to the track's origin, which is right for both.
   */
  viewingSpaceId?: ViewingSpaceId;
}

type TrackEditorContextType = {
  openTrackEditor: (config: openTrackEditorConfig) => void;
  closeTrackEditor: () => void;
};

const TrackEditorContext = createContext<TrackEditorContextType>({
  openTrackEditor: () => {},
  closeTrackEditor: () => {},
});

export const TrackEditorProvider = ({
  children,
  respectQueryParam,
}: {
  children: React.ReactNode;
  respectQueryParam?: boolean;
}) => {
  const { manageGlobalSingleSpaceWatcher, getGlobalSpace } = useGlobalSpaces();
  const history = useHistory();
  const { track: contextualTrack } = useTrack();
  // Only the `respectQueryParam` path below reads this: there the editor
  // opens for the ROUTE's own track, so the route's space is its viewing
  // space. Every other opener passes the space explicitly.
  const { spaceId: routeSpaceId } = useSpaceContext();
  const [isOpen, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<TRACK_EDITOR_TAB>(
    TRACK_EDITOR_TAB.DETAILS,
  );
  const [track, setTrack] = useState<Track>(undefined);
  const [viewingSpaceId, setViewingSpaceId] = useState<
    ViewingSpaceId | undefined
  >(undefined);

  useEffect(() => {
    const ids = Array.from(
      new Set([track?.spaceId, viewingSpaceId].filter(Boolean)),
    ) as string[];
    const attached = ids.filter((id) => !getGlobalSpace(id));
    attached.forEach((spaceId) =>
      manageGlobalSingleSpaceWatcher({
        action: "attach",
        componentId: "space-track-editor",
        spaceId,
      }),
    );
    return () => {
      attached.forEach((spaceId) =>
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          componentId: "space-track-editor",
          spaceId,
        }),
      );
    };
  }, [track?.spaceId, viewingSpaceId]);

  const openTrackEditor = useCallback(
    ({
      track,
      initialTab = TRACK_EDITOR_TAB.DETAILS,
      viewingSpaceId,
    }: openTrackEditorConfig) => {
      setTrack(track);
      setViewingSpaceId(viewingSpaceId);
      setInitialTab(initialTab);
      setOpen(true);
    },
    [],
  );

  const closeTrackEditor = useCallback(() => {
    setTrack(undefined);
    setViewingSpaceId(undefined);
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      openTrackEditor,
      closeTrackEditor,
    }),
    [openTrackEditor, closeTrackEditor],
  );

  useEffect(() => {
    if (respectQueryParam) {
      const params = new URLSearchParams(location.search);
      const editorParam = params.get("track-editor") as TRACK_EDITOR_TAB;
      const isParamValid =
        Object.values(TRACK_EDITOR_TAB).includes(editorParam);
      if (isParamValid && contextualTrack) {
        setTrack(contextualTrack);
        setViewingSpaceId(routeSpaceId);
        setInitialTab(editorParam);
        setOpen(true);
        params.delete("track-editor");
        history.replace({
          pathname: location.pathname,
          search: params.toString(),
        });
      }
    }
  }, [contextualTrack, history.location]);

  const space = getGlobalSpace(track?.spaceId);

  return (
    <TrackEditorContext.Provider value={value}>
      <FileEditorProvider>
        {isOpen &&
          (contextualTrack ? (
            <TrackEditor
              space={space}
              viewingSpaceId={viewingSpaceId}
              initialTab={initialTab}
              onClose={closeTrackEditor}
            />
          ) : (
            <TrackContextProvider id={track.id} viewingSpaceId={viewingSpaceId}>
              <TrackEditor
                space={space}
                viewingSpaceId={viewingSpaceId}
                initialTab={initialTab}
                onClose={closeTrackEditor}
              />
            </TrackContextProvider>
          ))}
        {children}
      </FileEditorProvider>
    </TrackEditorContext.Provider>
  );
};

export const useTrackEditor = () => useContext(TrackEditorContext);
